import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NativePermissionsService } from './native-permissions.service';

@Injectable({
    providedIn: 'root'
})
export class MicrophoneCleanupService {

    private routerSubscription: Subscription | null = null;
    private currentRoute: string = '';

    // Rutas que usan componentes de micrófono
    private microphoneRoutes = [
        '/chat',
        '/practice',
        '/learn/lesson',
        '/character',
        '/roleplaying',
        '/custom-conversation',
        '/initial-evaluation'
    ];

    constructor(
        private router: Router,
        private nativePermissionsService: NativePermissionsService
    ) {
        this.initializeRouteTracking();
    }

    /**
     * Inicializa el tracking de rutas para limpiar recursos automáticamente
     */
    private initializeRouteTracking(): void {
        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.handleRouteChange(event.urlAfterRedirects);
        });
    }

    /**
     * Maneja cambios de ruta y limpia recursos si es necesario
     */
    private async handleRouteChange(newRoute: string): Promise<void> {
        const previousRoute = this.currentRoute;
        this.currentRoute = newRoute;

        // Solo proceder si estamos en plataforma nativa
        if (!this.nativePermissionsService.isNativePlatform()) {
            return;
        }

        // Verificar si estamos saliendo de una ruta que usa micrófono
        const wasUsingMicrophone = this.microphoneRoutes.some(route =>
            previousRoute.includes(route)
        );

        const isUsingMicrophone = this.microphoneRoutes.some(route =>
            newRoute.includes(route)
        );

        console.log(`Cambio de ruta: ${previousRoute} -> ${newRoute}`);
        console.log(`Uso de micrófono: ${wasUsingMicrophone} -> ${isUsingMicrophone}`);

        // Si estamos saliendo de una ruta que usa micrófono y vamos a una que no lo usa
        if (wasUsingMicrophone && !isUsingMicrophone) {
            console.log('Liberando recursos de micrófono por cambio de ruta...');
            await this.cleanupMicrophoneResources();
        }

        // Si estamos entrando a una ruta que usa micrófono desde una que no lo usa
        else if (!wasUsingMicrophone && isUsingMicrophone) {
            console.log('Preparando recursos de micrófono para nueva ruta...');
            await this.prepareMicrophoneForNewRoute();
        }

        // Si estamos moviéndonos entre rutas que usan micrófono
        else if (wasUsingMicrophone && isUsingMicrophone && previousRoute !== newRoute) {
            console.log('Transición entre rutas que usan micrófono...');
            await this.handleMicrophoneRouteTransition();
        }
    }

    /**
     * Limpia todos los recursos de micrófono
     */
    private async cleanupMicrophoneResources(): Promise<void> {
        try {
            await this.nativePermissionsService.releaseCurrentStream();
            console.log('✅ Recursos de micrófono liberados por cambio de ruta');
        } catch (error) {
            console.error('Error liberando recursos de micrófono:', error);
        }
    }

    /**
     * Prepara el micrófono para una nueva ruta
     */
    private async prepareMicrophoneForNewRoute(): Promise<void> {
        try {
            // Esperar un poco para asegurar que la ruta anterior se haya limpiado completamente
            await new Promise(resolve => setTimeout(resolve, 500));

            // Verificar que el micrófono esté disponible
            const isAvailable = await this.nativePermissionsService.isMicrophoneAvailable();

            if (!isAvailable) {
                console.log('Micrófono no disponible inmediatamente, esperando...');
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            console.log('✅ Micrófono preparado para nueva ruta');
        } catch (error) {
            console.error('Error preparando micrófono para nueva ruta:', error);
        }
    }

    /**
     * Maneja transición entre rutas que usan micrófono
     */
    private async handleMicrophoneRouteTransition(): Promise<void> {
        try {
            // En transiciones entre rutas que usan micrófono, liberar y esperar un poco
            await this.nativePermissionsService.releaseCurrentStream();

            // Esperar más tiempo en transiciones para asegurar liberación completa
            const platform = this.nativePermissionsService.getPlatform();
            const waitTime = platform === 'ios' ? 2000 : 1500;

            console.log(`Esperando ${waitTime}ms para transición segura entre rutas con micrófono`);
            await new Promise(resolve => setTimeout(resolve, waitTime));

            console.log('✅ Transición de micrófono completada');
        } catch (error) {
            console.error('Error en transición de rutas con micrófono:', error);
        }
    }

    /**
     * Método manual para limpiar recursos (útil para componentes específicos)
     */
    async manualCleanup(): Promise<void> {
        if (this.nativePermissionsService.isNativePlatform()) {
            console.log('Limpieza manual de recursos de micrófono solicitada');
            await this.cleanupMicrophoneResources();
        }
    }

    /**
     * Método para verificar si una ruta usa micrófono
     */
    routeUsesMicrophone(route: string): boolean {
        return this.microphoneRoutes.some(micRoute => route.includes(micRoute));
    }

    /**
     * Método para añadir nuevas rutas que usan micrófono
     */
    addMicrophoneRoute(route: string): void {
        if (!this.microphoneRoutes.includes(route)) {
            this.microphoneRoutes.push(route);
            console.log(`Ruta ${route} añadida a rutas que usan micrófono`);
        }
    }

    /**
     * Método para obtener información de debugging
     */
    async getDebugInfo(): Promise<any> {
        const debugInfo = await this.nativePermissionsService.getDebugInfo();

        return {
            ...debugInfo,
            currentRoute: this.currentRoute,
            microphoneRoutes: this.microphoneRoutes,
            routeUsesMicrophone: this.routeUsesMicrophone(this.currentRoute),
            isTrackingRoutes: !!this.routerSubscription
        };
    }

    /**
     * Limpieza al destruir el servicio
     */
    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
            this.routerSubscription = null;
        }
    }
}